import type { VehicleLookupApiError, VehicleLookupResponse } from '../../types/vehicle-lookup.js';

export class VehicleCategoryMismatchError extends Error {
  constructor(
    public readonly recommendedAdModel: string,
    message?: string,
  ) {
    super(message || 'Vehicle category mismatch');
    this.name = 'VehicleCategoryMismatchError';
  }
}

type LookupVehicleParams = {
  regnr?: string;
  variantId?: string;
  csrfToken: string;
  root: string;
  model: string;
  isCategoryMismatchEnabled: boolean;
};

export const lookupVehicle = async ({
  regnr,
  variantId,
  csrfToken,
  root,
  model,
  isCategoryMismatchEnabled,
}: LookupVehicleParams) => {
  const response = await fetch(
    `${root}/api/vehicle-lookup/${model}/vehicleInformation/${regnr}${variantId ? `/${variantId}` : ''}`,
    {
      headers: {
        'x-csrf-token': csrfToken,
      },
    },
  );

  if (!response.ok) {
    // Check for category mismatch error if toggle is enabled
    if (isCategoryMismatchEnabled && response.status >= 400) {
      try {
        const errorData = (await response.json()) as VehicleLookupApiError;

        if (errorData.subErrorCode === 1001 && errorData.context?.recommendedAdModel) {
          throw new VehicleCategoryMismatchError(errorData.context.recommendedAdModel, errorData.reason);
        }
      } catch (error) {
        // Re-throw if it's our custom error, otherwise fall through
        if (error instanceof VehicleCategoryMismatchError) {
          throw error;
        }
      }
    }

    // Generic error for all other cases
    throw new Error('Failed to fetch vehicle information');
  }

  if (response.status === 204) {
    return null;
  }

  return (await response.json()) as VehicleLookupResponse;
};
